<svelte:options
  customElement={{
    tag: 'dynamic-svelte-chart',
    props: {
      columns: { type: 'Array' },
      dataset: { type: 'Array' },
      showFavouriteButton: { type: 'Boolean' },
      portal: { type: 'Object' },
      config: { type: 'Object' },
      dateRange: { type: 'Object' },
    },
    shadow: 'none',
  }}
/>

<script>
  import Highcharts from 'highcharts/esm/highcharts.js';

  import 'highcharts/esm/modules/accessibility';
  import 'highcharts/esm/modules/exporting';
  import 'highcharts/esm/modules/export-data';
  import './allowNegativeLog';

  import Chart from '@highcharts/svelte';

  import { getIsFavourited, toggleChartFavourite } from './common.svelte';

  let {
    id = '',
    showFavouriteButton = false,
    portal = {},
    config = {
      title: 'Chart',
      attribution: 'Chart',
      type: 'line',
      labelColumn: 'Year',
      xTitle: '',
      xAxisType: 'datetime',
      yTitle: '',
      stacking: 'null',
      logarithmic: false,
      hideMarkers: false,
      minimumYAxis: undefined,
    },
    columns = [],
    dataset = [],
    dateRange = $bindable({
      min: undefined,
      max: undefined,
    }),

    // events
    refreshFavourites = () => {},
  } = $props();

  let isFavourited = $derived(getIsFavourited(portal, id));

  let allowLogarithmic = $state(true);
  function parseNumber(number) {
    if (typeof number === 'number') {
      return number;
    }

    if (!number) {
      allowLogarithmic = false;
      return null;
    }

    const sanitized = number?.replace(/[^-\d.]/g, '');

    const parsed = Number(sanitized);

    if (parsed <= 0) {
      allowLogarithmic = false;
    }

    return parsed;
  }

  const YEAR_FORMAT = /^(\d{4})$/;
  const MONTH_YEAR_FORMAT = /^([A-z]{3})-(\d{2})$/;
  const YEAR_MONTH_FORMAT = /^(\d{2})-([A-z]{3})$/;
  const MONTH_MAPPING = {
    jan: '01',
    feb: '02',
    mar: '03',
    apr: '04',
    may: '05',
    jun: '06',
    jul: '07',
    aug: '08',
    sep: '09',
    oct: '10',
    nov: '11',
    dec: '12',
  };

  let dateFormat = $state('{value:%Y}');

  // Reformat dates as YYYY/mm/dd
  function formatDate(dateValue) {
    let matches = dateValue.match(YEAR_FORMAT);
    if (matches) {
      dateFormat = '{value:%Y}';
      return `${matches[1]}/01/01`;
    }

    matches = dateValue.match(MONTH_YEAR_FORMAT);
    if (matches) {
      dateFormat = '{value:%B %Y}';
      return `20${matches[2]}/${MONTH_MAPPING[matches[1].toLowerCase()]}/01`;
    }

    matches = dateValue.match(YEAR_MONTH_FORMAT);
    if (matches) {
      dateFormat = '{value:%B %Y}';
      return `20${matches[1]}/${MONTH_MAPPING[matches[2].toLowerCase()]}/01`;
    }

    return dateValue;
  }

  function parseDataSeries() {
    let typeOptions = {
      stacking: config.stacking === 'null' ? null : config.stacking,
    };

    const dataSeries = [];
    for (const column of columns) {
      const newSet = dataset
        .map((row) => [
          config.xAxisType === 'datetime'
            ? formatDate(row[config.labelColumn]?.toString())
            : row[config.labelColumn],
          parseNumber(row[column]),
        ])
        .filter((row) => !!row[1]);

      dataSeries.push({
        column,
        type: config.type,
        ...typeOptions,
        name: column,
        // tooltip: {
        //   valueSuffix: ` ${set.addendum}`,
        // },
        data: newSet,
      });
    }
    return dataSeries;
  }

  let options = $state({});
  $effect(() => {
    try {
      const dataSeries = parseDataSeries();

      let opt = {
        colors: [
          '#2caffe',
          // Disabling this color as it adds a weird outline effect in pie charts
          // '#544fc5',
          '#00e272',
          '#fe6a35',
          '#6b8abc',
          '#d568fb',
          '#2ee0ca',
          '#fa4b42',
          '#feb56a',
          '#91e8e1',
        ],
        title: {
          text: config.title,
          style: {
            fontSize: 24,
          },
        },
        subtitle: {
          text: config.attribution ? 'Source: ' + config.attribution : undefined,
        },
        xAxis: {
          type: config.xAxisType,
          title: {
            text: config.xTitle
              ? `${config.labelColumn} (${config.xTitle})`
              : config.labelColumn,
          },
          labels: {
            autoRotation: [-45],
          },
        },
        yAxis: {
          type: config.logarithmic ? 'logarithmic' : undefined,
          custom: {
            allowNegativeLog: true,
          },
          title: {
            text: config.yTitle,
          },
          min: config.minimumYAxis >= 0 ? config.minimumYAxis : undefined,
        },
        chart: {
          type: config.type,
          style: {
            fontSize: '1.6rem',
          },
          height: null,
        },
        plotOptions: {
          series: {
            marker: {
              enabled: config.hideMarkers ? false : undefined,
            },
          },
        },
        series: dataSeries,
      };

      if (config.xAxisType === 'datetime') {
        opt.xAxis.min = dateRange.min;
        opt.xAxis.max = dateRange.max;
        opt.xAxis.labels = { format: dateFormat };
        opt.xAxis.minTickInterval = 24 * 3600 * 1000;

        if (dateFormat === '{value:%B %Y}') {
          opt.xAxis.labels.rotation = -45;
        }
      }

      if (showFavouriteButton) {
        let svg = `<svg 
  xmlns="http://www.w3.org/2000/svg"
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="favourite-icon ${isFavourited ? 'favourited' : ''} feather feather-heart">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
</svg>`;
        opt.exporting = {
          buttons: {
            contextButton: {
              buttonSpacing: 35,
              menuItems: [
                'viewFullscreen',
                'printChart',
                'separator',
                'downloadPNG',
                'downloadSVG',
                'separator',
                'downloadCSV',
                'downloadXLS',
              ],
            },
            customButton: {
              useHTML: true,
              text: svg,
              onclick: function () {
                isFavourited = toggleChartFavourite(
                  portal,
                  id,
                  dateRange,
                  refreshFavourites
                );
              },
              height: 24,
              buttonSpacing: 35,
              // y: 24,
              x: -29,
            },
          },
        };
      }

      if (config.logarithmic && !allowLogarithmic) {
        console.warn(
          'Chart',
          opt.title.text,
          'was configured to use logarithmic scaling, but contained values <= 0. A workaround has been employed to fix this, but please be aware that negative values are not mathematically correct.'
        );
      }

      console.debug('Built chart options:', opt);

      options = opt;
    } catch (err) {
      console.error(err);
      options = { error: true };
    }
  });
</script>

<div class="dynamic-chart">
  {#if !options.error}
    <Chart {options} highcharts={Highcharts} />
  {:else}
    An error occurred.
  {/if}
</div>
